const scriptUrl = 'https://cdn.weglot.com/weglot.min.js';
let libraryPromise;
let initializationPromise;

function withTimeout(operation, milliseconds) {
    let timer;
    return Promise.race([
        operation,
        new Promise((resolve, reject) => {
            timer = setTimeout(() => reject(new Error('Weglot timeout')), milliseconds);
        }),
    ]).finally(() => clearTimeout(timer));
}

function loadLibrary() {
    if (window.Weglot) {
        return Promise.resolve(window.Weglot);
    }
    if (libraryPromise) {
        return libraryPromise;
    }

    // Lazy loading avoids a CDN dependency on the rest of the shop. Reuse an
    // existing integration's script rather than loading a second Weglot copy.
    const existing = Array.from(document.scripts).find(script => /^https:\/\/cdn\.weglot\.com\/weglot(?:\.min)?\.js(?:[?#]|$)/.test(script.src));
    const script = existing || document.createElement('script');
    let loaded;
    let failed;
    const operation = new Promise((resolve, reject) => {
        loaded = () => window.Weglot ? resolve(window.Weglot) : reject(new Error('Weglot unavailable'));
        failed = () => reject(new Error('Weglot script failed'));
        script.addEventListener('load', loaded);
        script.addEventListener('error', failed);
        if (!existing) {
            script.src = scriptUrl;
            script.async = true;
            document.head.appendChild(script);
        }
    });

    libraryPromise = withTimeout(operation, 3000)
        .catch(error => {
            libraryPromise = null;
            if (!existing) {
                script.remove();
            }
            throw error;
        })
        .finally(() => {
            script.removeEventListener('load', loaded);
            script.removeEventListener('error', failed);
        });
    return libraryPromise;
}

async function readyLibrary() {
    const library = await loadLibrary();
    if (library.initialized) {
        return library;
    }
    if (!initializationPromise) {
        let interval;
        const operation = new Promise((resolve, reject) => {
            interval = setInterval(() => {
                if (library.initialized) {
                    resolve(library);
                }
            }, 25);
            try {
                // This instance is only for explicit API calls. The existing
                // proxy integration owns page translations and language links.
                const initialization = library.initialize({
                    api_key: window.webienSite.weglot.apiKey,
                    hide_switcher: true,
                    auto_switch: false,
                    wait_transition: false,
                    excluded_blocks: [{ value: 'html' }],
                });
                if (initialization && typeof initialization.catch === 'function') {
                    initialization.catch(reject);
                }
                if (library.initialized) {
                    resolve(library);
                }
            } catch (error) {
                reject(error);
            }
        });
        initializationPromise = withTimeout(operation, 3000)
            .catch(error => {
                initializationPromise = null;
                throw error;
            })
            .finally(() => clearInterval(interval));
    }
    return initializationPromise;
}

function mapTexts(value, transform, key = '') {
    if (typeof value === 'string') {
        // Keep machine fields, codes, URLs, numbers and measurements intact.
        if (/^(id|sku|code|product_code|unit|url|image|image_url)$/i.test(key)
            || !/\p{L}/u.test(value)
            || /^(?:https?:\/\/|#[0-9a-f]{3,8}$)/i.test(value)
            || /^[A-Z]+[-_]?\d[\w.-]*$/.test(value)
            || /^\s*[\d.,\s×x/+%°-]+\s*(?:mm|cm|m|kg|g|st|pcs|°)?\s*$/i.test(value)) {
            return value;
        }
        return transform(value);
    }
    if (Array.isArray(value)) {
        return value.map(item => mapTexts(item, transform));
    }
    if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value).map(([name, item]) => [name, mapTexts(item, transform, name)]));
    }
    return value;
}

export async function translateSpecifications(specifications, locale) {
    // Use the cart market's language, not the SDK's language: the proxy can
    // serve cached Swedish HTML and our API-only SDK may report Swedish.
    const language = String(locale || 'sv-SE').split(/[-_]/)[0].toLowerCase();
    if (language === 'sv' || specifications == null) {
        return specifications;
    }

    const words = new Map();
    mapTexts(specifications, text => words.set(text, text));
    if (!words.size) {
        return specifications;
    }

    try {
        if (!window.webienSite?.weglot?.apiKey) {
            throw new Error('Weglot not configured');
        }
        const originals = [...words.keys()];
        if (originals.join('').length > 4500) {
            throw new Error('Specifications exceed translation request limit');
        }
        // One deadline covers loading, initialization and translation. Late
        // callbacks can settle their promise but never change a sent payload.
        const translated = await withTimeout((async () => {
            const library = await readyLibrary();
            return new Promise((resolve, reject) => {
                try {
                    const result = library.translate({
                        words: originals.map(w => ({ t: 1, w })),
                        languageTo: language,
                    }, resolve);
                    if (result && typeof result.then === 'function') {
                        result.then(resolve, reject);
                    }
                } catch (error) {
                    reject(error);
                }
            });
        })(), 6000);

        const texts = Array.isArray(translated) ? translated : translated?.to_words;
        if (!Array.isArray(texts) || texts.length !== originals.length
            || texts.some(text => typeof text !== 'string' || !text.trim())) {
            throw new Error('Invalid Weglot response');
        }
        originals.forEach((text, index) => words.set(text, texts[index]));
        return mapTexts(specifications, text => words.get(text));
    } catch (error) {
        // Translation must never prevent a purchase. Do not log the payload.
        console.warn('Specifications translation unavailable; using original text.');
        return specifications;
    }
}
